/**
 * Builds the v2-pipeline plan for cross-route, single-date `move`. Strictly
 * sequential source-then-destination so the daily/master mutations never
 * overlap. Source-side resequence is intentionally not emitted — BE
 * renumbers on unassign.
 */
import { type CseId, type WorkOrderId } from 'types/entities/shared/ids'
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'

import { dayOfWeekForServiceDate } from '../dayOfWeek'
import { makeAssignDailyStep } from '../steps/assignDaily'
import { makeAssignMasterStep } from '../steps/assignMaster'
import { makeCascadeFutureDailyStep } from '../steps/cascadeFutureDaily'
import { makePreflightStep } from '../steps/preflight'
import { makeSequenceDailyStep } from '../steps/sequenceDaily'
import { makeUnassignDailyStep } from '../steps/unassignDaily'
import { makeUnassignMasterStep } from '../steps/unassignMaster'
import { makeVerifyStep } from '../steps/verify'

import type { DailyStop, Deps, MoveIntent, OnRequestStop, OperationPlan, PipelineStep } from 'types/modules/routing'

const STRATEGY_ID = 'v2-pipeline' as const

const collectIds = (
  stops: ReadonlyArray<DailyStop | OnRequestStop>
): {
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
  readonly cseIds: ReadonlyArray<CseId>
} => {
  const workOrderIds: WorkOrderId[] = []
  const cseIds: CseId[] = []
  const seenWo = new Set<string>()
  const seenCse = new Set<string>()
  stops.forEach(stop => {
    if (!isDailyStop(stop) && !isOnRequestStop(stop)) return
    const { workOrderId } = stop
    if (workOrderId && !seenWo.has(workOrderId)) {
      seenWo.add(workOrderId)
      workOrderIds.push(workOrderId)
    }
    if (!isDailyStop(stop)) return
    const { configuredServiceEventId } = stop
    if (!configuredServiceEventId || seenCse.has(configuredServiceEventId)) return
    seenCse.add(configuredServiceEventId)
    cseIds.push(configuredServiceEventId)
  })
  return { workOrderIds, cseIds }
}

export const buildMovePipeline = (intent: MoveIntent, deps: Deps): OperationPlan => {
  const steps: PipelineStep[] = []
  const { workOrderIds, cseIds } = collectIds(intent.stops)
  const includeMaster = intent.includeMaster === true

  steps.push(makePreflightStep({ intent }, deps) as PipelineStep)

  if (workOrderIds.length > 0) {
    steps.push(
      makeUnassignDailyStep({ routeId: intent.sourceRouteId, workOrderIds }, deps) as PipelineStep,
      makeAssignDailyStep(
        {
          routeId: intent.routeId,
          serviceDate: intent.serviceDate,
          workOrderIds,
        },
        deps
      ) as PipelineStep
    )
  }

  if (intent.destinationOrderedStops && intent.destinationOrderedStops.length > 0) {
    steps.push(
      makeSequenceDailyStep(
        {
          routeId: intent.routeId,
          serviceDate: intent.serviceDate,
          orderedStops: intent.destinationOrderedStops,
        },
        deps
      ) as PipelineStep
    )
  }

  if (includeMaster && cseIds.length > 0) {
    const dayOfWeek = dayOfWeekForServiceDate(intent.serviceDate)
    if (dayOfWeek) {
      steps.push(
        makeUnassignMasterStep(
          {
            routeId: intent.sourceRouteId,
            dayOfWeek,
            cseIds,
            futureStops: false,
          },
          deps
        ) as PipelineStep,
        makeAssignMasterStep(
          {
            routeId: intent.routeId,
            dayOfWeek,
            cseIds,
            futureStops: true,
            futureStart: intent.serviceDate,
          },
          deps
        ) as PipelineStep
      )
    }
  }

  if (includeMaster && workOrderIds.length > 0) {
    steps.push(
      makeCascadeFutureDailyStep(
        {
          mode: 'assign',
          phase: 'ASSIGN_FUTURE_DAILY',
          routeId: intent.routeId,
          currentServiceDate: intent.serviceDate,
          workOrderIds,
        },
        deps
      ) as PipelineStep
    )
  }

  steps.push(
    makeVerifyStep(
      {
        intent,
        expectedAssignedWorkOrderIds: workOrderIds.map(String),
      },
      deps
    ) as PipelineStep
  )

  return {
    strategy: STRATEGY_ID,
    intent,
    steps,
  }
}
